import { createAuthClient } from 'better-auth/vue'
import { emailOTPClient } from 'better-auth/client/plugins'
import { AUTH_URL } from '@/config/auth'

export class NeedsEmailVerificationError extends Error {
  constructor(public email: string) {
    super('Email non vérifié')
    this.name = 'NeedsEmailVerificationError'
  }
}

const STORAGE_KEYS = {
  bearerToken: 'authBearerToken',
  jwtToken: 'authJwtToken',
} as const

export interface AuthUser {
  id: string
  name: string
  email: string
  emailVerified: boolean
  image: string | null
  firstName?: string | null
  lastName?: string | null
  phone?: string | null
  dateOfBirth?: string | null
  church?: string | null
  city?: string | null
  postalCode?: string | null
  address?: string | null
  gender?: string | null
  profession?: string | null
  role?: string | null
}

export interface AuthSessionPayload {
  user: AuthUser
  session: {
    id: string
    userId: string
    expiresAt: string | Date
  }
}

interface AuthState {
  session: AuthSessionPayload | null
  jwt: string | null
}

interface EmailCredentials {
  email: string
  password: string
}

interface SignUpInput extends EmailCredentials {
  firstName: string
  lastName: string
}

function canUseStorage() {
  return typeof window !== 'undefined'
}

function readToken(key: keyof typeof STORAGE_KEYS) {
  if (!canUseStorage()) {
    return null
  }

  return window.localStorage.getItem(STORAGE_KEYS[key])
}

function writeToken(key: keyof typeof STORAGE_KEYS, value: string | null) {
  if (!canUseStorage()) {
    return
  }

  if (value) {
    window.localStorage.setItem(STORAGE_KEYS[key], value)
  } else {
    window.localStorage.removeItem(STORAGE_KEYS[key])
  }
}

function syncTokensFromHeaders(headers: Headers) {
  const bearerToken = headers.get('set-auth-token')
  const jwtToken = headers.get('set-auth-jwt')

  if (bearerToken) {
    writeToken('bearerToken', bearerToken)
  }

  if (jwtToken) {
    writeToken('jwtToken', jwtToken)
  }
}

function clearStoredTokens() {
  writeToken('bearerToken', null)
  writeToken('jwtToken', null)
}

const ERROR_MESSAGES_FR: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: 'Identifiants incorrects',
  INVALID_PASSWORD: 'Mot de passe actuel incorrect',
  INVALID_EMAIL: 'Adresse email invalide',
  EMAIL_NOT_VERIFIED: 'Email non vérifié',
  USER_ALREADY_EXISTS: 'Cet email est déjà utilisé',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'Cet email est déjà utilisé',
  USER_NOT_FOUND: 'Utilisateur introuvable',
  PASSWORD_TOO_SHORT: 'Mot de passe trop court (8 caractères minimum)',
  PASSWORD_TOO_LONG: 'Mot de passe trop long',
  INVALID_OTP: 'Code invalide',
  OTP_EXPIRED: 'Code expiré, demandez-en un nouveau',
  TOO_MANY_ATTEMPTS: 'Trop de tentatives, veuillez demander un nouveau code',
}

function translateAuthError(
  error: { code?: string; message?: string } | null | undefined,
  fallback: string,
) {
  if (!error) return fallback
  if (error.code && ERROR_MESSAGES_FR[error.code]) return ERROR_MESSAGES_FR[error.code]
  return error.message || fallback
}

export function getStoredJwtToken() {
  return readToken('jwtToken')
}

export function getStoredBearerToken() {
  return readToken('bearerToken')
}

export function clearStoredAuthTokens() {
  clearStoredTokens()
}

export const betterAuthClient = createAuthClient({
  baseURL: AUTH_URL,
  plugins: [emailOTPClient()],
  fetchOptions: {
    auth: {
      type: 'Bearer',
      token: () => readToken('bearerToken') ?? undefined,
    },
    onSuccess: (ctx) => {
      syncTokensFromHeaders(ctx.response.headers)
    },
  },
})

export async function fetchAuthState(): Promise<AuthState> {
  const result = await betterAuthClient.getSession()
  if (result.error || !result.data) {
    clearStoredTokens()
    return {
      session: null,
      jwt: null,
    }
  }

  return {
    session: result.data as unknown as AuthSessionPayload,
    jwt: getStoredJwtToken(),
  }
}

export async function signInWithEmail(credentials: EmailCredentials) {
  const result = await betterAuthClient.signIn.email(credentials)
  if (result.error) {
    if (result.error.code === 'EMAIL_NOT_VERIFIED') {
      throw new NeedsEmailVerificationError(credentials.email)
    }
    throw new Error(translateAuthError(result.error, 'Identifiants incorrects'))
  }
}

export async function sendVerificationOtp(email: string) {
  const result = await betterAuthClient.emailOtp.sendVerificationOtp({
    email,
    type: 'email-verification',
  })
  if (result.error) {
    throw new Error(translateAuthError(result.error, "Impossible d'envoyer le code"))
  }
}

export async function verifyEmailOtp(email: string, otp: string) {
  const result = await betterAuthClient.emailOtp.verifyEmail({ email, otp })
  if (result.error) {
    throw new Error(translateAuthError(result.error, 'Code invalide'))
  }
}

export async function sendForgotPasswordOtp(email: string) {
  const result = await betterAuthClient.emailOtp.requestPasswordReset({ email })
  if (result.error) {
    throw new Error(translateAuthError(result.error, "Impossible d'envoyer le code"))
  }
}

export async function resetPasswordWithOtp(email: string, otp: string, password: string) {
  const result = await betterAuthClient.emailOtp.resetPassword({ email, otp, password })
  if (result.error) {
    throw new Error(translateAuthError(result.error, 'Impossible de réinitialiser le mot de passe'))
  }
}

export interface ProfileUpdateInput {
  firstName?: string
  lastName?: string
  phone?: string | null
  dateOfBirth?: string | null
  church?: string | null
  city?: string | null
  postalCode?: string | null
  address?: string | null
  gender?: 'male' | 'female' | null
  profession?: string | null
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const result = await betterAuthClient.changePassword({
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  })
  if (result.error) {
    throw new Error(translateAuthError(result.error, 'Impossible de modifier le mot de passe'))
  }
}

export async function updateProfile(input: ProfileUpdateInput) {
  const result = await betterAuthClient.updateUser(
    input as Parameters<typeof betterAuthClient.updateUser>[0],
  )
  if (result.error) {
    throw new Error(translateAuthError(result.error, 'Impossible de mettre à jour le profil'))
  }
}

export async function checkEmailExists(email: string): Promise<{ exists: boolean; emailVerified: boolean }> {
  const res = await fetch(`${AUTH_URL}/api/check-email?email=${encodeURIComponent(email)}`)
  if (!res.ok) throw new Error("Impossible de vérifier l'email, réessayez")
  return (await res.json()) as { exists: boolean; emailVerified: boolean }
}

export async function signUpWithEmail(input: SignUpInput) {
  const result = await betterAuthClient.signUp.email({
    email: input.email,
    password: input.password,
    name: `${input.firstName} ${input.lastName}`.trim(),
    firstName: input.firstName,
    lastName: input.lastName,
  } as Parameters<typeof betterAuthClient.signUp.email>[0])

  if (result.error) {
    throw new Error(translateAuthError(result.error, "Erreur lors de l'inscription"))
  }
}

export async function signOutFromAuth() {
  await betterAuthClient.signOut()
  clearStoredTokens()
}
